import { Component, Input, Type, ViewEncapsulation, OnInit, EventEmitter, Output, OnChanges, SimpleChanges } from '@angular/core';
import { SliderProOptions } from '../../slider-pro/slider-pro.component';
import { ScreenType } from 'projects/common/model/ScreenType';
import { FavoriteCardTypeEnum, UserFavoriteCardData } from '../user-favorite-data.model';
import { Currency } from 'projects/common/model/landingModel';
import { UserFavoriteCardComponent } from '../user-favorite-card/user-favorite-card.component';
import { SliderOptions } from '../../slider/slider/slider.component';
import { GridOptions } from '../../grid/grid.component';
import { EvaMessageOptions } from '../../eva-message/eva-message.component';
import { UpaTarget } from 'projects/common/services/upa.service';

@Component({
  selector: 'favorite-cluster',
  template: `
    <eva-message *ngIf="showEvaWarning"
      [data]="getEvaMessageData()"
      [moduleIndex]="moduleIndex"
      [uniqueId]="uniqueId">
    </eva-message>
    <div class="favorite-cluster-container" [ngClass]="!!sliderOptions ? 'favorite-cluster-container' : ''">
      <slider-pro *ngIf="!!sliderProOptions"
        [options]="sliderProOptions"
        [componentParams]="componentParams"
        [components]="components"
        [upaData]="upaData">
      </slider-pro>
      <grid *ngIf="!!gridOptions" [options]="gridOptions" [responsiveGrid]="true">
          <user-favorite-card *ngFor="let item of favorites | slice: 0 : maxGridItems; let idx = index"
            [data]="item"
            [locale]="locale"
            [flow]="flow"
            [currency]="currency"
            [isWhiteLabel]="isWhiteLabel"
            [showDeleteBtn]="showDeleteBtn && productIsNotCTA(item.product)"
            [showAlertBtn]="showAlertBtn && productIsNotCTA(item.product)"
            [cardType]="cardsType"
            [moduleId]="moduleId"
            [moduleIndex]="moduleIndex"
            [favoriteDeleted]="favoriteEmitter"
            [position]="idx"
            [nextFlow]="nextFlow"
            [uniqueId]="uniqueId"
            [target]="target"
            >
          </user-favorite-card>
      </grid>
      <div *ngIf="!!gridOptions && !allRowsFit">
          <eva-button [options]="{ghost_button: true, type: '-default', size: '-md', link_text: ('offers.buttons.seeMoreOffers.' + !seeMoreSelected) | translate}"
                      (clickEvent)="onSeeMoreOffersClick()" [moduleId]="moduleId" [moduleIndex]="moduleIndex" [uniqueId]="uniqueId"></eva-button>
      </div>
    </div>
  `,
  styleUrls: ['./favorite-cluster.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class FavoriteClusterComponent implements OnInit, OnChanges {
  static readonly FAVORITE_CTA: string = "FAVORITE_CTA";
  static readonly FAVORITE_CTA_URL: string = "/me/bookings?as_referer=home&current_tab=TRIP_PLANNER";

  @Input() favorites: UserFavoriteCardData[];
  @Input() moduleId: string;
  @Input() moduleIndex: number;
  @Input() uniqueId: string;
  @Input() locale: string;
  @Input() pageViewId: string;
  @Input() searchId: string;
  @Input() isWhiteLabel: boolean;
  @Input() flow: string;
  @Input() currency: Currency;
  @Input() showDeleteBtn: boolean;
  @Input() showAlertBtn: boolean;
  @Input() screenType: ScreenType;
  @Input() maxItems: number | undefined;
  @Input() cardsType: FavoriteCardTypeEnum;
  @Input() nextFlow: string;
  @Input() sliderOptions: SliderOptions;
  @Input() gridOptions: GridOptions;
  @Output() favoriteDeleted = new EventEmitter<string>();

  sliderProOptions: SliderProOptions;
  components: Type<any>[] = [];
  componentParams: any[] = [];

  itemsPerRow = 1.3;
  upaData: any;
  showSliderPro: boolean = false;
  allRowsFit = false;
  seeMoreSelected = false;
  showEvaWarning = false;
  nextStepIsCheckout = false;
  lastNonEmptyResults: UserFavoriteCardData[];
  maxGridItems: number;
  defaultGridItemsToShow: number;
  favoriteEmitter: EventEmitter<string>;
  target: UpaTarget = UpaTarget.FAVORITES;

  constructor() { }

  ngOnInit(): void {
    this.upaData = {
      moduleId: this.moduleId,
      moduleIndex: this.moduleIndex,
      uniqueId: this.uniqueId
    };
    this.favoriteEmitter = new EventEmitter<string>();
    this.favoriteDeleted.subscribe((favoriteId: string) => this.handleFavoriteDeleted(favoriteId))
    this.defaultGridItemsToShow = this.calculateItemsToShow();
    this.updateGrid();
    this.initSliderProOptions();
    this.transformModel();
    this.nextStepIsCheckout = this.nextFlow === 'CHECKOUT';
    if (this.nextStepIsCheckout && this.favorites && this.favorites.length > 0) {
      this.lastNonEmptyResults = this.favorites;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.seeMoreSelected = false;
    const favsChanges = changes["favorites"];
    if (!favsChanges) return;

    if (favsChanges.previousValue && favsChanges.currentValue.length == 0) {
      this.favorites = this.lastNonEmptyResults;
      this.showEvaWarning = this.lastNonEmptyResults.length > 0;
    } else {
      this.lastNonEmptyResults = favsChanges.currentValue;
      this.showEvaWarning = false;
    }
    this.defaultGridItemsToShow = this.calculateItemsToShow();
    this.updateGrid();
  }

  getEvaMessageData(): EvaMessageOptions {
    return {
      message_type: "-warning",
      icon: "warning",
      title: 'flightOffers.emptyFiltered.title',
      text: 'flightOffers.emptyFiltered.text.' + (this.screenType == ScreenType.DESKTOP ? 'desktop' : 'mobile')
    }
  }

  calculateItemsToShow(): number {
    return this.gridOptions ? Math.min(this.gridOptions.columns * this.gridOptions.rows, this.favorites.length) : 0;
  }

  updateGrid(): void {
    this.allRowsFit = this.favorites.length <= this.defaultGridItemsToShow;
    this.maxGridItems = this.calculateGridItemsToShow();
    this.updateGridOptionsRows();
  }

  calculateGridItemsToShow(): number {
    if (!this.gridOptions) return 0;
    if (this.seeMoreSelected) return this.favorites.length;
    return this.defaultGridItemsToShow;
  }

  updateGridOptionsRows(): void {
    if (!this.gridOptions) return;
    this.gridOptions = {
      columns: this.gridOptions.columns,
      gap: this.gridOptions.gap,
      rows: this.maxGridItems / this.gridOptions.columns
    }
  }

  initSliderProOptions(): void {
    if (!this.gridOptions) {
      if (!this.sliderOptions) {
        const isReduced = this.cardsType === FavoriteCardTypeEnum.REDUCED;
        // cards a restar por haber menos espacio de ancho de pantalla
        const cardsToHide = globalThis.innerWidth <= 1279 ? 1 : 0;
        this.sliderProOptions = {
          itemsToShow: { 'DEFAULT': this.screenType !== 'DESKTOP' ? this.itemsPerRow : isReduced ? 3 - cardsToHide : 4 - cardsToHide },
          itemsToMove: { 'DEFAULT': 1 },
          fixedWidth: { 'DEFAULT': this.screenType !== 'DESKTOP' ? 324 : undefined },
          dragToMove: { 'DEFAULT': this.screenType !== 'DESKTOP' },
          showArrows: { 'DEFAULT': this.screenType === 'DESKTOP' },
          showDots: { 'DEFAULT': true },
          dotsLighten: false,
          arrowsInnerContainer: false
        };
      } else {
        this.sliderProOptions = {
          itemsToShow: { 'DEFAULT': this.sliderOptions.items_to_show },
          itemsToMove: { 'DEFAULT': this.sliderOptions.items_to_move },
          fixedWidth: { 'DEFAULT': this.sliderOptions.max_fixed_width },
          dragToMove: { 'DEFAULT': this.screenType !== 'DESKTOP' },
          showArrows: { 'DEFAULT': this.sliderOptions.show_arrows },
          showDots: { 'DEFAULT': this.sliderOptions.show_dots },
          dotsLighten: false,
          arrowsInnerContainer: this.sliderOptions.arrows_inner_container
        }
      }
    }
  }

  onSeeMoreOffersClick(): void {
    this.seeMoreSelected = !this.seeMoreSelected;
    this.updateGrid();
  }

  transformModel(): void {
    // Agrego card CTA si tengo el maximo de items
    if (this.maxItems && this.favorites.length === this.maxItems && this.screenType !== 'DESKTOP') {
      const cardCTA: UserFavoriteCardData = {
        id: FavoriteClusterComponent.FAVORITE_CTA,
        product: FavoriteClusterComponent.FAVORITE_CTA,
        link: {
          title: 'userFavorites.CTABtn.desktop',
          url: FavoriteClusterComponent.FAVORITE_CTA_URL
        }

      } as UserFavoriteCardData;

      this.favorites = [...this.favorites, cardCTA];
    }

    this.components = new Array(this.favorites.length).fill(UserFavoriteCardComponent);
    this.componentParams = this.favorites.map((favorite: UserFavoriteCardData, index: number) => {
      return {
        data: favorite,
        locale: this.locale,
        flow: this.flow,
        currency: this.currency,
        isWhiteLabel: this.isWhiteLabel,
        showDeleteBtn: this.showDeleteBtn && this.productIsNotCTA(favorite.product),
        showAlertBtn: this.showAlertBtn && this.productIsNotCTA(favorite.product),
        cardType: this.cardsType,
        moduleId: this.moduleId,
        moduleIndex: this.moduleIndex,
        favoriteDeleted: this.favoriteEmitter,
        position: index
      }
    });
  }

  productIsNotCTA(product: string): boolean {
    return product !== FavoriteClusterComponent.FAVORITE_CTA
  }

  handleFavoriteDeleted(favoriteId: string): void {
    this.favorites = this.favorites.filter(favorite => favorite.id !== favoriteId);

    // Elimino el card CTA
    if (this.favorites.some(favorite => favorite.product === FavoriteClusterComponent.FAVORITE_CTA)) {
      this.favorites = this.favorites.filter(favorite => favorite.product !== FavoriteClusterComponent.FAVORITE_CTA);
    }
    this.favoriteDeleted.emit(favoriteId);
    this.transformModel();
  }
}
